import { ProductService } from '@dav-core/services/product.service';
import { Markup } from '@dav-model/login/response/user.response.model';
import { ProductFilters } from '@dav-model/product-filters/product-filters';
import { GetProductsRequest } from '@dav-model/request/product.request';
import { GetProductsResponse, ProductItem } from '@dav-model/response/getproducts.response';
import { SurchargeSelectionState } from '@dav-model/surcharge-selection-state.model';

import * as ProductActions from './product.actions';

export interface State {
  products: GetProductsResponse;
  request: GetProductsRequest;
  grid: ProductItem[];
  originalGrid: ProductItem[];
  filters: ProductFilters | undefined;
  surcharges: { name: string; selected: boolean }[] | undefined;
  markup: Markup;
  error: string;
  successful: boolean;
  surchargesSelection: SurchargeSelectionState[] | undefined;
}

const InitialState: State = {
  products: undefined,
  markup: new Markup(),
  request: null,
  grid: undefined,
  originalGrid: undefined,
  filters: undefined,
  surcharges: undefined,
  error: '',
  successful: false,
  surchargesSelection: undefined,
};

export function reducer(state = InitialState, action: ProductActions.All): State {
  switch (action.type) {
    case ProductActions.GET_PRODUCTS: {
      return {
        ...state,
        request: action.payload,
        error: '',
      };
    }

    case ProductActions.CHANGE_MARKUP: {
      if (state.grid) {
        const markupProducts = ProductService.productResponseDataMapper(state.grid);

        return {
          ...state,
          markup: action.payload,
          grid: markupProducts,
        };
      } else {
        return {
          ...state,
          markup: action.payload,
        };
      }
    }

    case ProductActions.GET_PRODUCTS_SUCCESS: {
      return {
        ...state,
        products: action.payload.response,
        originalGrid: action.payload.gridProducts,
        grid: action.payload.gridProducts,
        filters: action.payload.filters,
        markup: action.payload.markup,
        error: '',
        successful: true,
        surchargesSelection: undefined,
      };
    }

    case ProductActions.ADD_MARKUP_TO_COST: {
      return {
        ...state,
        grid: action.payload.grid,
        markup: action.payload.markup,
      };
    }

    case ProductActions.GET_PRODUCTS_FAILURE: {
      return {
        ...state,
        products: undefined,
        error: action.payload.error,
        successful: false,
      };
    }

    case ProductActions.FILTER_GRID_SUCCESS: {
      const markupProducts = ProductService.productResponseDataMapper(action.payload.grid);

      return {
        ...state,
        grid: markupProducts,
        filters: action.payload.filters,
      };
    }

    case ProductActions.LOAD_PRODUCT_QUOTE: {
      return {
        ...state,
        products: action.payload.response,
        grid: action.payload.gridProducts,
        filters: action.payload.filters,
        markup: action.payload.markup,
        request: action.payload.request,
        successful: true,
      };
    }

    case ProductActions.SORT_GRID: {
      return {
        ...state,
        grid: action.payload,
      };
    }

    case ProductActions.RESET_PRODUCTS: {
      return {
        ...InitialState,
      };
    }

    case ProductActions.RESET_SURCHARGES_SELECTION: {
      return {
        ...state,
        surchargesSelection: undefined,
      };
    }

    case ProductActions.SET_SURCHARGES_SELECTION: {
      return {
        ...state,
        surchargesSelection: action.surchargesSelection,
      };
    }

    case ProductActions.APPLY_SURCHARGES:
      let updatedState: State = {} as State;
      const grid = onApplySurcharges(state.grid || [], state.surchargesSelection || []);
      if (state.products?.availableOptions?.products?.productItem?.length && state.grid?.length) {
        updatedState = {
          ...state,
          products: {
            ...state.products,
            availableOptions: {
              ...state.products.availableOptions,
              products: {
                productItem: onApplySurcharges(
                  state.products.availableOptions.products.productItem,
                  state.surchargesSelection
                ),
              },
            },
          },
          grid,
        };
      } else {
        updatedState = { ...state };
      }

      return updatedState;

    default: {
      return state;
    }
  }
}

function onApplySurcharges(
  productItems: ProductItem[],
  surchargesSelection: { name: string; selected: boolean }[]
): ProductItem[] {
  return JSON.parse(JSON.stringify(productItems)).map((productItem: ProductItem) => {
    productItem.surchargeAdded = false;
    surchargesSelection.forEach((selection) => {
      const matchingInProduct = productItem.surchargeOptions.surchargeOption.filter(
        ({ label, key }) => (label.enUsLabel || label.frCaLabel) === selection.name || selection.name === key
      );
      if (matchingInProduct.length === 1) {
        const optionInProduct = matchingInProduct[0];
        if (optionInProduct.added === undefined || optionInProduct.added === false) {
          if (selection.selected) {
            productItem.retailRate += +optionInProduct.surchargeMsrpAmount;
            productItem.customerCost += +optionInProduct.surchargeMsrpAmount;
            productItem.dealerCost += +optionInProduct.surchargeAmount;
            if (productItem.retailRateType === 'NON-REG') {
              productItem.costPlaceholder += +optionInProduct.surchargeMsrpAmount;
            }
          }
        } else if (selection.selected === false) {
          productItem.retailRate -= +optionInProduct.surchargeMsrpAmount;
          productItem.customerCost -= +optionInProduct.surchargeMsrpAmount;
          productItem.dealerCost -= +optionInProduct.surchargeAmount;
          if (productItem.retailRateType === 'NON-REG') {
            productItem.costPlaceholder -= +optionInProduct.surchargeMsrpAmount;
          }
        }
        optionInProduct.added = selection.selected;
        if (selection.selected) {
          productItem.surchargeAdded = true;
        }
      }
    });

    return productItem;
  });
}
